import { notification } from 'antd'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType, Ticket } from '@types'

import { TicketsApi } from './api'

const useApproveTicket = () => {
  const { t } = useTranslation()
  const tPreffix = 'admin_router.admin_dashboard.use_approve_tickets.'

  const onError = (err: unknown) => {
    const error = err as AxiosErrorType

    const errorResponse = error.data.error as {
      type: string
      status: number
      message: string
    }

    if (errorResponse.type === 'NO_ACTIVE_ROUTE') {
      const hint =
        errorResponse.message.length > 62
          ? `${t(`${tPreffix}no_active_route_hint`)} ${errorResponse.message.substring(
              62,
              errorResponse.message.length,
            )}`
          : t(`${tPreffix}no_active_route_at_all_hint`)

      notification.error({
        message: `${t(`${tPreffix}no_active_route`)} ${hint}`,
      })
    } else {
      notification.error({
        message: t(`${tPreffix}error`),
      })
    }
  }

  const onSuccess = (id: number) => {
    notification.success({
      message: [t(`${tPreffix}success`), ` ID ${id}`],
    })
    queryClient.cancelQueries(QUERY_KEYS.TICKETS)
    queryClient.refetchQueries(QUERY_KEYS.TICKETS)
  }

  const queryClient = useQueryClient()

  const onApproveSuccess = (response: { ticket: Ticket }) => {
    onSuccess(response.ticket.id)
    queryClient.cancelQueries(QUERY_KEYS.TO_AUTHORIZE)
    queryClient.refetchQueries(QUERY_KEYS.TO_AUTHORIZE)
  }
  const onApproveError = (err: AxiosError) => {
    onError(err)
    queryClient.invalidateQueries(QUERY_KEYS.TICKETS)
  }

  const { mutate, isLoading } = useMutation(TicketsApi.approveTicket, {
    onSuccess: onApproveSuccess,
    onError: onApproveError,
  })

  return {
    approveTicket: mutate,
    isLoading,
  }
}

export { useApproveTicket }
